import React from 'react';
import { UserRedemptionEngagement } from '../../../services/analyticsService';
import Icon from '../../../components/AppIcon';

interface UserEngagementLeaderboardProps {
  data: UserRedemptionEngagement[];
  title: string;
}

const UserEngagementLeaderboard: React.FC<UserEngagementLeaderboardProps> = ({ data, title }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    if (diffDays <= 30) return `${Math.ceil((diffDays - 1) / 7)} weeks ago`;
    return `${Math.ceil((diffDays - 1) / 30)} months ago`;
  };

  const getRankIcon = (index: number) => {
    switch (index) {
      case 0: return { icon: 'Crown', color: 'text-yellow-500' };
      case 1: return { icon: 'Medal', color: 'text-gray-400' };
      case 2: return { icon: 'Award', color: 'text-orange-500' };
      default: return { icon: 'User', color: 'text-muted-foreground' };
    }
  };

  const getCategoryColor = (category: string) => {
    const categoryColors: Record<string, string> = {
      'Attractions': 'bg-blue-100 text-blue-800',
      'Restaurants & Cafés': 'bg-orange-100 text-orange-800',
      'Shopping/Retail': 'bg-purple-100 text-purple-800',
      'Beauty & Wellness': 'bg-pink-100 text-pink-800',
      'Activities & Experiences': 'bg-green-100 text-green-800',
      'Entertainment & Nightlife': 'bg-red-100 text-red-800',
      'Transport & Mobility': 'bg-gray-100 text-gray-800',
      'Local Services': 'bg-indigo-100 text-indigo-800',
      'Kids & Family': 'bg-yellow-100 text-yellow-800',
    };
    return categoryColors[category] || 'bg-gray-100 text-gray-800';
  };

  const totalSavings = data.reduce((sum, user) => sum + user.totalSavings, 0);
  const totalRedemptions = data.reduce((sum, user) => sum + user.totalRedemptions, 0);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Users with highest redemption savings and engagement
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-muted/30 rounded-lg">
        <div className="text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Savings</p>
          <p className="text-lg font-semibold text-foreground">{formatCurrency(totalSavings)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Redemptions</p>
          <p className="text-lg font-semibold text-foreground">{totalRedemptions.toLocaleString('de-AT')}</p>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="space-y-3">
        {data.slice(0, 15).map((user, index) => {
          const rankInfo = getRankIcon(index);
          const savingsPercentage = totalSavings > 0 ? (user.totalSavings / totalSavings) * 100 : 0;
          
          return (
            <div 
              key={user.userId} 
              className={`flex items-center space-x-4 p-3 rounded-lg border transition-all duration-200 hover:shadow-md ${
                index < 3 ? 'border-primary/20 bg-primary/5' : 'border-border bg-muted/30'
              }`}
            >
              {/* Rank */}
              <div className="flex-shrink-0 w-8 text-center">
                {index < 3 ? (
                  <Icon name={rankInfo.icon} size={20} className={rankInfo.color} />
                ) : (
                  <span className="text-sm font-medium text-muted-foreground">
                    {index + 1}
                  </span>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-foreground truncate">
                    {user.userEmail || 'Anonymous User'}
                  </p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(user.favoriteCategory)}`}>
                    {user.favoriteCategory}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                  <span>{user.totalRedemptions} redemptions</span>
                  <span>Last: {formatDate(user.lastRedemptionDate)}</span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-muted rounded-full h-1.5 mb-1">
                  <div 
                    className="bg-primary h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(savingsPercentage * 5, 100)}%` }}
                  ></div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">
                    {formatCurrency(user.totalSavings)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatCurrency(user.averageRedemptionValue)} avg
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {data.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
          <p className="text-muted-foreground">No user engagement data available.</p>
        </div>
      )}

      {data.length > 15 && (
        <div className="mt-4 text-center">
          <p className="text-xs text-muted-foreground">
            Showing top 15 of {data.length} active users
          </p>
        </div>
      )}
    </div>
  );
};

export default UserEngagementLeaderboard;